import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth, db } from '../config/firebase';
import { collection, addDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';

export interface HomeworkFile {
  id: string;
  name: string;
  url: string;
  uploadedBy: string;
  uploadedAt: Date;
  size: number;
}

export class HomeworkService {
  private static getStoragePath(sessionId: number): string {
    return `batch9/homework_live${sessionId}`;
  }

  // Upload homework file
  static async uploadHomework(
    sessionId: number, 
    file: File, 
    userId: string,
    userEmail: string
  ): Promise<HomeworkFile> {
    const storagePath = this.getStoragePath(sessionId);
    // Use UID for filename prefix
    const fileName = `${userEmail}_${file.name}`;
    const fileRef = ref(storage, `${storagePath}/${fileName}`);

    console.log('Uploading homework:', {
      sessionId,
      userId,
      fileName,
      storagePath,
      fullPath: `${storagePath}/${fileName}`
    });

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    console.log('Current Firebase user:', currentUser);
    console.log('User ID from auth:', currentUser?.uid);
    console.log('User ID passed to function:', userId);

    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    // Force token refresh to ensure we have a valid token
    try {
      await currentUser.getIdToken(true);
      console.log('Token refreshed successfully');
    } catch (tokenError) {
      console.error('Error refreshing token:', tokenError);
      throw new Error('Failed to refresh authentication token');
    }

    try {
      console.log('Starting upload to Storage...');
      console.log('File ref path:', fileRef.fullPath);
      console.log('File size:', file.size);
      console.log('File type:', file.type);
      
      const snapshot = await uploadBytes(fileRef, file);
      console.log('Upload successful, getting download URL...');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Download URL obtained:', downloadURL);

      // Store file metadata in Firestore
      console.log('Storing metadata in Firestore...');
      const fileMetadata = {
        fileName: fileName,
        originalName: file.name,
        url: downloadURL,
        uploadedBy: userId,
        sessionId: sessionId,
        uploadedAt: new Date(),
        size: file.size,
        storagePath: `${storagePath}/${fileName}`
      };

      const docRef = await addDoc(collection(db, 'homework'), fileMetadata);
      console.log('Metadata stored in Firestore with ID:', docRef.id);

      return {
        id: fileName,
        name: file.name,
        url: downloadURL,
        uploadedBy: userId,
        uploadedAt: new Date(),
        size: file.size,
      };
    } catch (error) {
      console.error('Error uploading homework:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      throw new Error('Failed to upload homework file');
    }
  }

  // Get all homework files for a session
  static async getHomeworkFiles(sessionId: number): Promise<HomeworkFile[]> {
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return [];
    }

    try {
      // Query Firestore for user's homework files in this session
      const q = query(
        collection(db, 'homework'),
        where('uploadedBy', '==', currentUser.uid),
        where('sessionId', '==', sessionId)
      );

      const querySnapshot = await getDocs(q);
      const files: HomeworkFile[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        files.push({
          id: data.fileName,
          name: data.originalName,
          url: data.url,
          uploadedBy: data.uploadedBy,
          uploadedAt: data.uploadedAt.toDate(),
          size: data.size,
        });
      });

      return files.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
    } catch (error) {
      console.error('Error getting homework files:', error);
      return [];
    }
  }

  // Delete homework file (only by the user who uploaded it)
  static async deleteHomework(sessionId: number, fileName: string, userEmail: string): Promise<void> {
    const storagePath = this.getStoragePath(sessionId);
    const fileRef = ref(storage, `${storagePath}/${fileName}`);

    try {
      // Check if the file belongs to the user
      if (!fileName.startsWith(userEmail + '_')) {
        throw new Error('You can only delete your own homework files');
      }

      // Delete from Storage
      await deleteObject(fileRef);

      // Delete from Firestore
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User is not authenticated');
      }

      const q = query(
        collection(db, 'homework'),
        where('fileName', '==', fileName),
        where('uploadedBy', '==', currentUser.uid),
        where('sessionId', '==', sessionId)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });

    } catch (error) {
      console.error('Error deleting homework:', error);
      throw new Error('Failed to delete homework file');
    }
  }

  // Format file size for display
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
} 